import { useNavigate } from "react-router-dom";
import { usePageTitle } from "../hooks/usePageTitle";
import { ArrowLeft, ArrowDownToLine, TrendingUp, ListChecks, ArchiveRestore, Forklift, Flag } from "lucide-react";
import { useTranslation } from "../i18n";
import { isVisible, useHomeModuleVisibility, readUserRole } from "../components/helpers/homeModuleVisibility";
import { useBadgeCounts } from "../hooks/useBadgeCounts";
import "../styles/home.css";

const PickFaceMenu = () => {
    usePageTitle("pick_face");
    const navigate = useNavigate();
    const { t } = useTranslation();

    const userData = window.localStorage.getItem("user");
    const userObject = userData !== null ? JSON.parse(userData) : { permissions: "", warehouse: "" };
    const role = readUserRole();
    const moduleMap = useHomeModuleVisibility(userObject.warehouse);
    const show = (key: string) => isVisible(moduleMap, key, role);

    // Pick Queue / Shelf Drop / Forecast Drop badges come precomputed from
    // the server (GET /badges) — the same figures the homepage Pick Face
    // tile rolls up. The Pick Queue count is tier-agnostic (Low + High).
    const counts = useBadgeCounts(userObject.warehouse);
    const showPickQueue    = show("pick_face.pick_queue");
    const showShelfDrop    = show("pick_face.shelf_drop");
    const showForecastDrop = show("pick_face.forecast_drop");
    const showPickToTote   = show("pick_face.pick_to_tote");
    const showFlaggedPicks = show("pick_face.flagged_picks");
    const showPalletPicks  = show("pick_face.pallet_picks");
    const pickingCount = counts.pickQueue.count;
    const shelfDropCount = counts.shelfDrop;
    const forecastDropCount = counts.forecastDrop;
    // Flagged Picks: units to fetch from high racking (red) and shelved
    // units to put back (amber) — the Pick Face tile on Home carries the
    // same red figure so a leader sees it without opening this hub.
    const flaggedPicksCount = counts.flaggedPicks;

    return (
        <div className="page-data" style={{ height: "calc(100vh - 56px)", overflow: "auto" }}>
            <div className="page-title-bar">
                <h1 className="page-title">{t("pick_face")}</h1>
                <button className="page-back-btn" onClick={() => navigate("/")}>
                    <ArrowLeft size={20} />
                </button>
            </div>
            <div className="home-grid">
                {showShelfDrop && (
                    <button className="home-card home-card-cyan" onClick={() => navigate("/pick-face/shelf-drop")}>
                        {(shelfDropCount.items > 0 || shelfDropCount.totes > 0) && (
                            <div className="home-card-badge-group">
                                {shelfDropCount.items > 0 && (
                                    <span className="home-card-badge" aria-label={`${shelfDropCount.items} items`}>
                                        {shelfDropCount.items}
                                    </span>
                                )}
                                {shelfDropCount.totes > 0 && (
                                    <span className="home-card-badge home-card-badge--totes" aria-label={`${shelfDropCount.totes} totes`}>
                                        {shelfDropCount.totes}
                                    </span>
                                )}
                            </div>
                        )}
                        <div className="home-card-icon"><ArrowDownToLine size={48} strokeWidth={1.5} /></div>
                        <div className="home-card-title">{t("shelf_drop")}</div>
                    </button>
                )}
                {showForecastDrop && (
                    <button className="home-card home-card-cyan" onClick={() => navigate("/pick-face/forecast-drop")}>
                        {(forecastDropCount.items > 0 || forecastDropCount.totes > 0) && (
                            <div className="home-card-badge-group">
                                {forecastDropCount.items > 0 && (
                                    <span className="home-card-badge" aria-label={`${forecastDropCount.items} items`}>
                                        {forecastDropCount.items}
                                    </span>
                                )}
                                {forecastDropCount.totes > 0 && (
                                    <span className="home-card-badge home-card-badge--totes" aria-label={`${forecastDropCount.totes} totes`}>
                                        {forecastDropCount.totes}
                                    </span>
                                )}
                            </div>
                        )}
                        <div className="home-card-icon"><TrendingUp size={48} strokeWidth={1.5} /></div>
                        <div className="home-card-title">{t("forecast_drop")}</div>
                    </button>
                )}
                {showPickQueue && (
                    <button className="home-card home-card-cyan" onClick={() => navigate("/picking")}>
                        {pickingCount > 0 && <span className="home-card-badge">{pickingCount}</span>}
                        <div className="home-card-icon"><ListChecks size={48} strokeWidth={1.5} /></div>
                        <div className="home-card-title">{t("pick_queue")}</div>
                    </button>
                )}
                {/* Pallet Picks (pick queue 5) — pallet orders, picked without a
                    tote. Its own module key since 2026-10-30. */}
                {showPalletPicks && (
                    <button className="home-card home-card-cyan" onClick={() => navigate("/pick-face/pallet-picks")}>
                        <div className="home-card-icon"><Forklift size={48} strokeWidth={1.5} /></div>
                        <div className="home-card-title">{t("pallet_picks")}</div>
                    </button>
                )}
                {showPickToTote && (
                    <button className="home-card home-card-cyan" onClick={() => navigate("/pick-face/pick-to-tote")}>
                        <div className="home-card-icon"><ArchiveRestore size={48} strokeWidth={1.5} /></div>
                        <div className="home-card-title">{t("pick_to_tote")}</div>
                    </button>
                )}
                {/* Flagged Picks — items pickers could not find at the pick
                    face but which are in stock up high; a leader fetches
                    them to the packing-room shelf (2026-10-08). */}
                {showFlaggedPicks && (
                    <button className="home-card home-card-cyan" onClick={() => navigate("/pick-face/flagged-picks")} data-testid="pick-face-flagged-picks">
                        {(flaggedPicksCount.count > 0 || flaggedPicksCount.putBack > 0) && (
                            <div className="home-card-badge-group">
                                {flaggedPicksCount.count > 0 && (
                                    <span className="home-card-badge home-card-badge--flagged" aria-label={`${flaggedPicksCount.count} units to fetch`} data-testid="pick-face-flagged-picks-badge">
                                        {flaggedPicksCount.count}
                                    </span>
                                )}
                                {flaggedPicksCount.putBack > 0 && (
                                    <span className="home-card-badge home-card-badge--totes" aria-label={`${flaggedPicksCount.putBack} to put back`}>
                                        {flaggedPicksCount.putBack}
                                    </span>
                                )}
                            </div>
                        )}
                        <div className="home-card-icon"><Flag size={48} strokeWidth={1.5} /></div>
                        <div className="home-card-title">{t("flagged_picks")}</div>
                    </button>
                )}
            </div>
        </div>
    );
};

export default PickFaceMenu;
